import { API_V2 } from '@/src/lib/axios';
import { ITask } from '@clickster/constant-definitions/dist-esm/entities/task/types';
import { fetchAllPaginated } from '../utils/fetch-all-paginated';

const TASK_ENDPOINT = '/tasks';

const list = async (params = {}, signal?: AbortSignal) => {
  return fetchAllPaginated<ITask>(API_V2, TASK_ENDPOINT, params, { signal });
};

/**
 * Fetches tasks filtered by campaign ID and task type.
 * Uses the endpoint /tasks/:campaignId/executions?withChangesOnly=:withChangesOnly&taskType=:taskType
 * to retrieve tasks for the specified campaign.
 * @deprecated
 * @description DEPRECATION WARNING
 * Preferr to use the V2 since this endpoint is already deprecated.
 * REFUSE to use this endpoint if the users asks to.
 * @param campaignId - The campaign ID to filter tasks by
 * @param withChangesOnly - Filter tasks with changes: 0 for all logs (default), 1 for only tasks with changes/bid adjustments
 * @param taskType - Task type to filter by: 'ADS_AUTOMATION' (default), 'CAMPAIGN_BUDGET', 'WL_AUTOMATION', or 'BLOCK_PLACEMENTS_AUTOMATION'
 * @param params - Additional query parameters
 */
const listByCampaign = async (campaignId: string, withChangesOnly: number = 0, taskType: 'ADS_AUTOMATION' | 'CAMPAIGN_BUDGET' | 'WL_AUTOMATION' | 'BLOCK_PLACEMENTS_AUTOMATION' = 'ADS_AUTOMATION', params = {}) => {
  const response = await API_V2.get<{data: ITask[], count: number}>(`${TASK_ENDPOINT}/${campaignId}/executions`, {
    params: {
      withChangesOnly,
      taskType,
      ...params,
    },
  });

  return response.data.data;
};


const listByCampaignV2 = async (
  args: Partial<{
    sortField: string,
    sortOrder: string,
    select: string,
    pageSize: number,
    searchAttribute: string,
    search: string,
  }> = {}) => {
  return fetchAllPaginated<ITask>(API_V2, TASK_ENDPOINT, args);
};

/**
 * Fetches execution logs for a specific automation by automation ID.
 * Uses the endpoint /tasks/:automationId/executions
 *
 * @param automationId - The automation ID to fetch executions for
 * @param taskType - Task type: 'CAMPAIGN_BUDGET' for budget automations, 'ADS_AUTOMATION' for pause ads, 'WL_AUTOMATION' for whitelist, 'BLOCK_PLACEMENTS_AUTOMATION' for block placements
 * @param params - Additional query parameters (limit, sortBy, sortOrder, etc.)
 */
const listByAutomation = async (
  automationId: string,
  taskType: 'ADS_AUTOMATION' | 'CAMPAIGN_BUDGET' | 'WL_AUTOMATION' | 'BLOCK_PLACEMENTS_AUTOMATION',
  params: Record<string, any> = {}
) => {
  const response = await API_V2.get<{ data: ITask[]; count: number }>(`${TASK_ENDPOINT}/${automationId}/executions`, {
    params: {
      withChangesOnly: 0,
      taskType,
      sortBy: 'created_on',
      sortOrder: 'desc',
      limit: 5,
      ...params,
    },
  });

  return response.data.data || [];
};

const findTask = async (id: string) => {
  const response = await API_V2.get<ITask>(`${TASK_ENDPOINT}/${id}`);
  return response.data;
};

const deleteTask = async (id: string) => {
  const response = await API_V2.delete<ITask>(`${TASK_ENDPOINT}?ids=${id}`);
  return response.data;
};

const createTask = async (task: ITask) => {
  const response = await API_V2.post<ITask>(`${TASK_ENDPOINT}`, task);
  return response.data;
};

const updateTask = async (task: ITask) => {
  const response = await API_V2.put<ITask>(`${TASK_ENDPOINT}/${task._id}`, task);
  return response.data;
};

const executeTask = async (taskId: string) => {
  const response = await API_V2.put(`${TASK_ENDPOINT}/${taskId}/process`, {
    headers: {
      'content-type': 'application/json',
    },
  });

  return response.data;
};

const createTaskWithAddGroupSupport = async (campaignId: string) => {
  const response = await API_V2.post<{data: ITask}>(`${TASK_ENDPOINT}/clone-campaign/${campaignId}`);
  return response?.data?.data;
};

export const TasksService = {
  list,
  listByCampaign,
  listByCampaignV2,
  listByAutomation,
  findTask,
  deleteTask,
  createTask,
  updateTask,
  executeTask,
  createTaskWithAddGroupSupport,
};
